import type { OverviewDay, OverviewStatuses } from "@/lib/api/envoy";

/**
 * The example a visitor is shown on the places an account keeps: one
 * made-up person, Jordan Lee, and the jobs they are working through, the
 * same on every page, so Resume, Applications, Saved and Envoy tell one
 * story. Every company and person here is invented, and every page that
 * shows it says so. Pure data: nothing here is read from a service.
 */

/** A job in the example, as a row names it. */
export interface ExampleJob {
    title: string;
    company: string;
    where: string;
}

/** The job the example resume is tailored to. */
export const EXAMPLE_JOB: ExampleJob = {
    title: "Staff Platform Engineer",
    company: "tessel",
    where: "Remote, US",
};

/** What the example job asks for, as the tailor reads it. */
export const EXAMPLE_JOB_ASKS: readonly string[] = [
    "Kubernetes at scale",
    "Terraform",
    "Leading platform work",
];

/** One line of the resume: as Jordan wrote it, and as it reads tailored to the example job. */
export interface ExampleLine {
    original: string;
    tailored: string;
}

export interface ExampleRole {
    title: string;
    company: string;
    span: string;
    lines: readonly ExampleLine[];
}

export interface ExampleResume {
    name: string;
    headline: string;
    where: string;
    summary: ExampleLine;
    roles: readonly ExampleRole[];
    /** The skills in Jordan's order, and in the order the tailor put them for the job. */
    skills: { original: readonly string[]; tailored: readonly string[] };
}

/**
 * Jordan's resume. The tailored lines reword and reorder what is already
 * there and add nothing Jordan has not done, as the tailor works.
 */
export const EXAMPLE_RESUME: ExampleResume = {
    name: "Jordan Lee",
    headline: "Platform Engineer",
    where: "Denver, CO",
    summary: {
        original:
            "Platform engineer with eight years building cloud infrastructure and developer tooling.",
        tailored:
            "Platform engineer with eight years building Kubernetes platforms on AWS and the tooling teams ship with.",
    },
    roles: [
        {
            title: "Senior Platform Engineer",
            company: "Larkspur Freight",
            span: "2021 to now",
            lines: [
                {
                    original:
                        "Moved deploys to Argo CD, cutting release time from a day to 20 minutes.",
                    tailored:
                        "Led the move to Argo CD, which cut release time from a day to 20 minutes.",
                },
                {
                    original: "Ran 40 Kubernetes clusters on AWS, all changed through Terraform.",
                    tailored:
                        "Owned the Kubernetes platform: 40 clusters on AWS, every change made through Terraform.",
                },
                {
                    original: "Mentored four engineers and led the on-call rotation.",
                    tailored: "Mentored four engineers and led the on-call rotation.",
                },
            ],
        },
        {
            title: "Platform Engineer",
            company: "Bramble Health",
            span: "2018 to 2021",
            lines: [
                {
                    original: "Built the CI pipelines 30 teams deploy with, on GitHub Actions.",
                    tailored: "Built the CI pipelines 30 teams deploy with, on GitHub Actions.",
                },
            ],
        },
    ],
    skills: {
        original: ["Python", "Go", "AWS", "Terraform", "Kubernetes", "Argo CD"],
        tailored: ["Kubernetes", "Terraform", "AWS", "Argo CD", "Go", "Python"],
    },
};

export type ResumeVersionView = "original" | "tailored";

/** A line as the chosen version shows it, and whether the tailor changed it. */
export interface ShownLine {
    text: string;
    changed: boolean;
}

export function shownLine(line: ExampleLine, view: ResumeVersionView): ShownLine {
    const changed = line.original !== line.tailored;
    return {
        text: view === "tailored" ? line.tailored : line.original,
        changed: view === "tailored" && changed,
    };
}

/** Whether the tailor changed the order of the skills. */
export function skillsReordered(resume: ExampleResume): boolean {
    return resume.skills.original.join("\u0000") !== resume.skills.tailored.join("\u0000");
}

/** How many things the tailor changed for the example job: lines reworded, and the skills reordered. */
export function changeCount(resume: ExampleResume): number {
    const lines = [resume.summary, ...resume.roles.flatMap((role) => role.lines)];
    const reworded = lines.filter((line) => line.original !== line.tailored).length;
    return reworded + (skillsReordered(resume) ? 1 : 0);
}

/** What the tailor changed, in a line: how many things, for which job. */
export function changeSummary(resume: ExampleResume, job: ExampleJob): string {
    return `${changeCount(resume)} changes for ${job.title} at ${job.company}`;
}

/** Where an application stands in the example, as the Applications page groups them. */
export type ExampleApplicationStage = "ready" | "preparing" | "sent";

export interface ExampleApplication {
    job: ExampleJob;
    stage: ExampleApplicationStage;
    /** When, as the row says it. */
    when: string;
}

/** Jordan's applications: one ready to send (the example job), one being tailored, one sent. */
export const EXAMPLE_APPLICATIONS: readonly ExampleApplication[] = [
    { job: EXAMPLE_JOB, stage: "ready", when: "tailored this morning" },
    {
        job: {
            title: "Senior Site Reliability Engineer",
            company: "meridian",
            where: "Hybrid, Austin",
        },
        stage: "preparing",
        when: "started 4 minutes ago",
    },
    {
        job: { title: "Infrastructure Engineer, Compute", company: "orbital", where: "Remote, US" },
        stage: "sent",
        when: "sent 3 days ago",
    },
];

/** Where a saved job stands in the example. */
export type ExampleSavedState = "open" | "tailoring" | "closed";

export interface ExampleSaved {
    job: ExampleJob;
    state: ExampleSavedState;
    saved: string;
}

/**
 * Jordan's shortlist: one still open to tailor, the one being tailored
 * (the same job Applications has preparing), and one the company closed.
 */
export const EXAMPLE_SAVED: readonly ExampleSaved[] = [
    {
        job: { title: "Platform Engineer", company: "ridgeline", where: "Hybrid, Salt Lake City" },
        state: "open",
        saved: "saved 2 hours ago",
    },
    {
        job: EXAMPLE_APPLICATIONS[1].job,
        state: "tailoring",
        saved: "saved yesterday",
    },
    {
        job: { title: "Cloud Engineer III", company: "fabrikam", where: "Remote, US" },
        state: "closed",
        saved: "saved 5 days ago",
    },
];

/** Jordan's envoy, on: what it looks for, and what it has done this week. */
export const EXAMPLE_ENVOY = {
    lookingFor: [
        "Staff Platform Engineer",
        "Senior Platform Engineer",
        "Remote",
        "United States",
        "Senior, Staff",
    ],
    tiles: [
        { label: "New matches, 7 days", value: "12" },
        { label: "Ready for you", value: "1" },
        { label: "Applied, 7 days", value: "1" },
    ],
    /** The newest matches, as the morning email lists them. */
    matches: [
        EXAMPLE_JOB,
        EXAMPLE_APPLICATIONS[1].job,
        EXAMPLE_SAVED[0].job,
    ] as readonly ExampleJob[],
} as const;

/**
 * Jordan's Overview: every match the envoy ever found, wherever it stands
 * now. One ready (the example job), one being tailored, one that needs
 * Jordan, and a week of matches and sends that agree with the envoy's
 * tiles.
 */
export const EXAMPLE_OVERVIEW_STATUSES: OverviewStatuses = {
    matched: 18,
    tailoring: 1,
    ready: 1,
    needs_you: 1,
    applied: 6,
    failed: 0,
    skipped: 9,
    closed: 5,
};

/** The past two weeks, oldest first: the last seven add up to the envoy's 12 matches and 1 sent. */
const EXAMPLE_DAYS: readonly Omit<OverviewDay, "date">[] = [
    { matched: 3, tailored: 0, applied: 0, skipped: 1 },
    { matched: 2, tailored: 1, applied: 1, skipped: 0 },
    { matched: 4, tailored: 0, applied: 0, skipped: 1 },
    { matched: 1, tailored: 1, applied: 1, skipped: 0 },
    { matched: 0, tailored: 0, applied: 0, skipped: 0 },
    { matched: 2, tailored: 1, applied: 1, skipped: 1 },
    { matched: 3, tailored: 0, applied: 0, skipped: 0 },
    { matched: 2, tailored: 0, applied: 0, skipped: 1 },
    { matched: 1, tailored: 1, applied: 0, skipped: 0 },
    { matched: 3, tailored: 0, applied: 0, skipped: 0 },
    { matched: 0, tailored: 0, applied: 1, skipped: 0 },
    { matched: 1, tailored: 1, applied: 0, skipped: 1 },
    { matched: 2, tailored: 0, applied: 0, skipped: 0 },
    { matched: 3, tailored: 1, applied: 0, skipped: 1 },
];

/** Sends in the last seven days, as the Applied tile says them. */
export const EXAMPLE_APPLIED_LAST_7_DAYS = EXAMPLE_DAYS.slice(-7).reduce(
    (sum, day) => sum + day.applied,
    0,
);

/** The two weeks ending on `today`, so the chart's dates are always this fortnight's. */
export function exampleOverviewDays(today: Date): OverviewDay[] {
    const last = EXAMPLE_DAYS.length - 1;
    return EXAMPLE_DAYS.map((day, index) => {
        const date = new Date(
            Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() - (last - index)),
        );
        return { ...day, date: date.toISOString().slice(0, 10) };
    });
}

/** Tailors Jordan has spent this month: the ready, the one being tailored, and two earlier. */
export const EXAMPLE_TAILORS_USED = 4;

/** What waits on Jordan, as the Overview's table lists it. */
export const EXAMPLE_WAITING: readonly { job: ExampleJob; chip: string; line: string }[] = [
    { job: EXAMPLE_JOB, chip: "Ready", line: "tailored this morning" },
    {
        job: { title: "Senior Platform Engineer", company: "lumen", where: "Remote, US" },
        chip: "Needs you",
        line: "the form asks for a work sample",
    },
];
